function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, useRef, useReducer } from 'react';
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import { FunctionalLoader, useDidUpdate } from 'seagull';
import { Page, PageContentHeader, Typography, useStateInRef } from 'seagull';
import ErrorBoundaryPage from "../../components/ErrorBoundaryPage/ErrorBoundaryPage";
import { useOverlayContext, useStore, useStoreActions } from "../../contexts";
import { routes } from "../../constants";
import { useOnMount } from "../../hooks/useOnMount";
import { selectors } from "../../store/state-selectors";
import AgreementView from "./AgreementView/AgreementView";
import InvoicesView from "./InvoicesView/InvoicesView";
import ApproveAllBar from "./ApproveAll/ApproveAllBar";
import ActivityLog from "./ActivityLog/ActivityLog";
import AgreementMinisiteTopBar from "./AgreementMinisiteTopBar/AgreementMinisiteTopBar";
import PaymentMethods from "./PaymentMethods/PaymentMethods";
import BillingHub from "./BillingHub/BillingHub";
import styles from "./AgreementMinisite.module.scss";
import { FilterType } from "../AgreementsDashboard/agreementsDashboardFilters";
import { relationshipModel, relationshipSummaryModel } from "../../models";
import FeatureFlagsRelationshipContext from "../../components/FeatureFlagsRelationshipContext";
import { BIRelationshipScopeProvider } from "../../providers/BIRelationshipScopeProvider";
const minisiteTabs = {
  billingHub: {
    url: 'billing-hub',
    label: 'Billing Hub',
    supportsSample: false
  },
  agreement: {
    url: 'agreement',
    label: 'Agreement',
    supportsSample: true
  },
  paymentMethods: {
    url: 'payment-methods',
    label: 'Payment Methods',
    supportsSample: false
  },
  invoices: {
    url: 'invoices',
    label: 'Invoices',
    supportsSample: false
  },
  activity: {
    url: 'activity',
    label: 'Activity Log',
    supportsSample: false
  }
};
function ActualMinisite(props) {
  const {
    relationship,
    refresh,
    match
  } = props;
  const getProps = useStateInRef(props);
  const location = useLocation();
  const numAmendments = relationshipModel.numAmendments(relationship);
  const amountOfPendingInvoices = relationshipModel.numPendingInvoices(relationship);
  const amountOfPendingCharges = relationshipModel.numPendingCharges(relationship);
  const userIsVendor = useStore(selectors.user.isVendorInRelationship(relationship));
  const history = useHistory();
  const overlayContext = useOverlayContext();
  const scrollContainerRef = useRef(null);
  const [dismissedApproveAll, setDismissedApproveAll] = useState(false);
  const [tabRefreshKey, remountCurrentTab] = useReducer(state => state + 1, 0);
  const [billingHubKey, agreementViewKey, paymentMethodsKey, invoicesViewKey, activityLogKey] = new Array(5).fill(tabRefreshKey).map((val, i) => val + i);
  const scrollElRef = useRef(null);
  const relationshipScope = {
    id: relationship.id,
    isApproved: relationshipModel.isApproved(relationship)
  };
  const shouldShowApproveAllModal = !dismissedApproveAll && !userIsVendor && relationshipModel.isApproved(relationship) && numAmendments + amountOfPendingInvoices + amountOfPendingCharges > 1;
  const currentTabUrl = match.path === location.pathname ? null : (location.pathname.match(new RegExp(match.url + '/([^/]*)')) || {})[1];
  useDidUpdate(() => {
    const el = scrollElRef.current;
    if (el) {
      el.scrollTop = 0;
    }
  }, [location.pathname]);
  useDidUpdate(() => {
    getProps().refresh();
  }, [match.params.relationshipId]);
  const currentTab = Object.keys(minisiteTabs).find(tabName => minisiteTabs[tabName].url === currentTabUrl);
  useOnMount(() => {
    var _location$state;
    if (!currentTab) {
      const path = relationshipModel.isApproved(relationship) ? minisiteTabs.billingHub.url : minisiteTabs.agreement.url;
      history.replace("".concat(match.url, "/").concat(path));
    }
    if ((_location$state = location.state) !== null && _location$state !== void 0 && _location$state.scrollToBottom && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  });
  const agreementUrl = "".concat(match.url, "/").concat(minisiteTabs.agreement.url);
  const paymentMethodsUrl = "".concat(match.url, "/").concat(minisiteTabs.paymentMethods.url);
  const displayedTitle = relationshipModel.displayedTitle(relationship, userIsVendor);
  return /*#__PURE__*/React.createElement(BIRelationshipScopeProvider, {
    relationshipScope: relationshipScope
  }, /*#__PURE__*/React.createElement(Page, {
    "data-testid": "minisite-page",
    ref: scrollElRef,
    header: /*#__PURE__*/React.createElement(React.Fragment, null, relationshipModel.opposingPartyName(relationship, userIsVendor), displayedTitle && /*#__PURE__*/React.createElement(Typography, {
      as: "span",
      variant: "h4",
      ellipsis: true
    }, "\xA0\u2014 ", displayedTitle)),
    onClickBack: () => {
      if (relationshipModel.isApproved(relationship) || relationshipModel.isTerminated(relationship)) {
        const agreementStatusKey = FilterType.AgreementStatus;
        const searchParams = relationshipModel.isApproved(relationship) ? {} : {
          [agreementStatusKey]: relationshipSummaryModel.AgreementStatus.Terminated
        };
        const search = "?".concat(new URLSearchParams(searchParams));
        history.push({
          pathname: routes.agreementsDashboard,
          search
        });
      } else {
        history.push(routes.proposalsDashboard);
      }
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.main,
    "data-testid": "agreement-minisite"
  }, /*#__PURE__*/React.createElement(ErrorBoundaryPage, {
    bounded: true
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, shouldShowApproveAllModal && /*#__PURE__*/React.createElement(ApproveAllBar, {
    agreement: relationship,
    amendments: numAmendments,
    invoices: amountOfPendingInvoices,
    charges: amountOfPendingCharges,
    onClose: _ref => {
      let {
        status
      } = _ref;
      if (status === 'success') {
        refresh();
        remountCurrentTab();
        overlayContext.showSuccessToast('Approved successfully', 'approve-all-success-toast');
        if (!shouldShowApproveAllModal) {
          setDismissedApproveAll(true);
        }
      } else if (status === 'error') {
        overlayContext.showErrorToast('An error occurred. Try again or contact the vendor.');
      } else if (status === 'dismissed') {
        setDismissedApproveAll(true);
      }
    }
  }), /*#__PURE__*/React.createElement(AgreementMinisiteTopBar, {
    relationship: relationship,
    tabs: minisiteTabs
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.errorBoundary
  }, /*#__PURE__*/React.createElement(ErrorBoundaryPage, {
    bounded: true
  }, (!relationship.isSample || minisiteTabs.billingHub.supportsSample) && /*#__PURE__*/React.createElement(Route, {
    path: "".concat(match.url, "/").concat(minisiteTabs.billingHub.url),
    exact: true,
    key: billingHubKey,
    render: props => /*#__PURE__*/React.createElement(BillingHub, _extends({
      isVendor: userIsVendor,
      agreement: relationship,
      refreshAgreement: refresh
    }, props))
  }), (!relationship.isSample || minisiteTabs.agreement.supportsSample) && /*#__PURE__*/React.createElement(Route, {
    path: agreementUrl,
    key: agreementViewKey
  }, /*#__PURE__*/React.createElement("div", {
    ref: scrollContainerRef,
    className: styles.content,
    "data-testid": 'agreement-minisite-agreement-tab'
  }, /*#__PURE__*/React.createElement(AgreementView, {
    relationship: relationship,
    isVendor: userIsVendor,
    refresh: refresh
  }))), (!relationship.isSample || minisiteTabs.invoices.supportsSample) && /*#__PURE__*/React.createElement(Route, {
    path: "".concat(match.url, "/").concat(minisiteTabs.invoices.url),
    key: invoicesViewKey,
    render: props => /*#__PURE__*/React.createElement(InvoicesView, _extends({
      key: "invoices-view-".concat(dismissedApproveAll) //This is a dirty trick to refresh the data after approving invoices
      ,
      agreement: relationship,
      viewMode: userIsVendor ? 'vendor' : 'customer',
      refreshAgreement: refresh
    }, props))
  }), (!relationship.isSample || minisiteTabs.billingHub.supportsSample) && /*#__PURE__*/React.createElement(Route, {
    path: "".concat(match.url, "/").concat(minisiteTabs.activity.url),
    key: activityLogKey,
    render: props => /*#__PURE__*/React.createElement(ActivityLog, _extends({
      agreement: relationship
    }, props))
  }), (!relationship.isSample || minisiteTabs.paymentMethods.supportsSample) && /*#__PURE__*/React.createElement(Route, {
    path: paymentMethodsUrl,
    key: paymentMethodsKey,
    render: () => /*#__PURE__*/React.createElement(PaymentMethods, {
      isVendor: userIsVendor,
      agreement: relationship,
      onRefreshAgreement: refresh
    })
  })))))));
}
export default function AgreementMinisite(props) {
  const getStateInRef = useStateInRef(props);
  const {
    api
  } = useStoreActions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [relationship, setRelationship] = useState();
  useOnMount(() => {
    document.title = 'Agreement';
    refresh();
  });
  const refresh = async options => {
    const {
      silent
    } = options || {};
    const {
      match
    } = getStateInRef();
    !silent && setLoading(true);
    setError(undefined);
    try {
      const relationship = await api.relationship.get(match.params.relationshipId, {
        resolvePaymentMethod: true
      });
      setRelationship(relationship);
      return relationship;
    } catch (err) {
      console.error(err);
      setError(err);
      setRelationship(undefined);
    } finally {
      !silent && setLoading(false);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FunctionalLoader, {
    show: loading
  }), relationship && (relationshipModel.isApproved(relationship) || relationshipModel.isTerminated(relationship) ? /*#__PURE__*/React.createElement(FeatureFlagsRelationshipContext, {
    vendorId: relationship.vendor.id
  }, /*#__PURE__*/React.createElement(ActualMinisite, _extends({}, props, {
    relationship: relationship,
    refresh: refresh
  }))) : /*#__PURE__*/React.createElement(Redirect, {
    to: routes.home
  })), error && /*#__PURE__*/React.createElement(Redirect, {
    to: routes.home
  }));
}